import { RULE_GRAPH_MAX_ZOOM } from "./ruleGraphViewport/RULE_GRAPH_MAX_ZOOM";
import type { RuleGraphDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { Box, Stack, useTheme } from "@mui/material";
import {
  Background,
  getNodesBounds,
  ReactFlow,
  useReactFlow,
  useStore,
  useViewport,
} from "@xyflow/react";
import {
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import { animate, useReducedMotion } from "motion/react";
import { useImmer } from "use-immer";

import {
  RULE_GRAPH_VIEWPORT_CONTROL_BOTTOM_INSET,
  RULE_GRAPH_VIEWPORT_CONTROL_RIGHT_INSET,
  RULE_GRAPH_VIEWPORT_CONTROL_TOP_INSET,
} from "./ruleGraphControlDimensions/RuleGraphControlInsets";
import { initialRuleGraphViewport } from "./ruleGraphViewport/initialRuleGraphViewport";
import { ruleLaneDelay } from "./ruleLaneDelay";
import { RuleGraphBackToStart } from "./RuleGraphBackToStart";
import { RuleGraphZoomControl } from "./RuleGraphZoomControl";
import { RULE_GRAPH_EDGE_TYPES } from "./ruleGraphEdgeTypes";
import { ruleGraphRoutedEdgePath } from "./ruleGraphRoutedEdgePath";
import { getRuleGraphFlowEdges } from "./ruleGraphFlowModel/getRuleGraphFlowEdges";
import { getRuleGraphFlowNodes } from "./ruleGraphFlowModel/getRuleGraphFlowNodes";
import { RULE_GRAPH_NODE_TYPES } from "./ruleGraphNodeTypes";
import { RuleGraphNodeDetailsPanel } from "./RuleGraphNodeDetailsPanel";
import { RuleGraphDetailsPanelSync } from "./RuleGraphDetailsPanelSync";
import { RuleGraphRunCamera } from "./RuleGraphRunCamera";
import { analyzeRuleGraph } from "./analysis";
import { layoutRuleGraph } from "./layout";
import {
  createGraphLayoutExecutor,
  useRuleGraphLayout,
} from "./useRuleGraphLayout";
import { RuleGraphViewportSync } from "./RuleGraphViewportSync";
import { buildGraphLayoutRequest } from "./layout-request";
import type {
  RuleGraphOverlaysByEdgeId,
  RuleGraphOverlaysByNodeId,
} from "./types";

import "@xyflow/react/dist/style.css";

/**
 * The read-only rule-graph canvas: pan/zoom/click, not an editor. Editing
 * happens in chat or code; this surface exists for comprehension, so drag and
 * connect stay off — leaving them on would imply an editor that is not here.
 *
 * Selection is our own state (a details panel beside the canvas), not React
 * Flow's, mirroring the trace graph.
 */
const RuleGraphViewportControls = ({
  showBackToStart,
  minZoom,
  bounds,
  onViewportMoved,
  onViewportReset,
}: {
  showBackToStart: boolean;
  minZoom: number;
  bounds?: { x: number; y: number; width: number; height: number };
  onViewportMoved: () => void;
  onViewportReset: () => void;
}) => {
  const { getNodes, setViewport, zoomIn, zoomOut } = useReactFlow();
  const { zoom } = useViewport();
  const canvasWidth = useStore((state) => state.width);
  const canvasHeight = useStore((state) => state.height);
  const zoomPercentage = Math.round(zoom * 100);

  return (
    <>
      <Box
        sx={{
          bottom: RULE_GRAPH_VIEWPORT_CONTROL_BOTTOM_INSET,
          position: "absolute",
          right: RULE_GRAPH_VIEWPORT_CONTROL_RIGHT_INSET,
          zIndex: 5,
        }}
      >
        <RuleGraphZoomControl
          onZoomIn={() => {
            onViewportMoved();
            void zoomIn({ duration: 120 });
          }}
          onZoomOut={() => {
            onViewportMoved();
            void zoomOut({ duration: 120 });
          }}
          zoomInDisabled={zoom >= RULE_GRAPH_MAX_ZOOM}
          zoomOutDisabled={zoom <= minZoom}
          zoomPercentage={zoomPercentage}
        />
      </Box>
      {showBackToStart ? (
        <Box
          sx={{
            position: "absolute",
            right: RULE_GRAPH_VIEWPORT_CONTROL_TOP_INSET,
            top: RULE_GRAPH_VIEWPORT_CONTROL_TOP_INSET,
            zIndex: 5,
          }}
        >
          <RuleGraphBackToStart
            onClick={() => {
              void setViewport(
                initialRuleGraphViewport(
                  bounds ?? getNodesBounds(getNodes()),
                  { width: canvasWidth, height: canvasHeight },
                  minZoom
                ),
                { duration: 200 }
              );
              onViewportReset();
            }}
          />
        </Box>
      ) : null}
    </>
  );
};

export const RuleGraphView = ({
  graph: inputGraph,
  overlaysByNodeId,
  overlaysByEdgeId,
  cartShareByLaneId,
  skeleton = false,
  revealBuilt = false,
}: {
  graph: RuleGraphDto;
  overlaysByNodeId?: RuleGraphOverlaysByNodeId;
  overlaysByEdgeId?: RuleGraphOverlaysByEdgeId;
  /**
   * Live traffic: each lane's share of the rule's carts (0-100), keyed by the
   * lane header's node id. Absent until the rule has received traffic, and
   * then no lane shows the badge.
   */
  cartShareByLaneId?: Record<string, number>;
  /**
   * Building state: the graph's structure with shimmer bars for text — no
   * overlays, no selection, no details panel.
   */
  skeleton?: boolean;
  /** Let completed Atlas lanes appear in rule order. */
  revealBuilt?: boolean;
}) => {
  const theme = useTheme();
  const [selectedNodeId, setSelectedNodeId] = useImmer<string | null>(null);
  const [hasViewportMoved, setHasViewportMoved] = useImmer(false);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const detailsPanelId = useId();
  const analysis = analyzeRuleGraph(inputGraph);
  const graph = analysis.graph;
  const layout = layoutRuleGraph(graph, analysis);
  const [minZoom, setMinZoom] = useImmer(0.35);
  const [appliedRevision, setAppliedRevision] = useImmer("");
  const [measurements, setMeasurements] = useImmer<
    Record<string, { width: number; height: number }>
  >({});
  const onMeasure = (id: string, size: { width: number; height: number }) => {
    setMeasurements((draft) => {
      if (
        draft[id]?.width !== size.width ||
        draft[id]?.height !== size.height
      ) {
        draft[id] = size;
      }
    });
  };
  useEffect(() => {
    if (
      selectedNodeId !== null &&
      !graph.nodes.some((node) => node.id === selectedNodeId)
    ) {
      setSelectedNodeId(null);
    }
  }, [graph, selectedNodeId, setSelectedNodeId]);

  const rawNodes = getRuleGraphFlowNodes({
    graph,
    layout,
    onMeasure,
    selectedNodeId: skeleton ? null : selectedNodeId,
    onSelectNode: (nodeId) =>
      setSelectedNodeId((current) => (current === nodeId ? null : nodeId)),
    detailsPanelId,
    overlaysByNodeId,
    skeleton,
    cartShareByLaneId,
  });
  const rawEdges = getRuleGraphFlowEdges(
    graph,
    theme,
    skeleton ? undefined : overlaysByEdgeId,
    skeleton ? undefined : overlaysByNodeId,
    layout
  );
  const request = buildGraphLayoutRequest({
    analysis,
    nodes: rawNodes,
    edges: rawEdges,
    measurements,
    overlaysByNodeId,
    skeleton,
  });
  const { boxes, graphKey } = request;
  const geometry = useRuleGraphLayout(request, createGraphLayoutExecutor);
  const canvasReady =
    geometry.geometry !== null && appliedRevision === geometry.revision;
  const nodes = rawNodes.map((node, index) => ({
    ...node,
    ...boxes[index],
    measured: { width: boxes[index]!.width, height: boxes[index]!.height },
    position:
      geometry.geometry?.positionsByNodeId.get(node.id) ?? node.position,
  }));
  const edges = rawEdges.map((edge) => ({
    ...edge,
    type: "ruleGraphRouted",
    data: {
      ...edge.data,
      path: ruleGraphRoutedEdgePath({
        path: geometry.geometry?.pathsByEdgeId.get(edge.id) ?? "",
        allowCurve:
          edge.sourceHandle === "flow-source" &&
          edge.targetHandle === "flow-target",
        obstacles: nodes
          .filter((node) => node.id !== edge.source && node.id !== edge.target)
          .map((node) => ({
            ...node.position,
            width: node.width!,
            height: node.height!,
          })),
      }),
    },
  }));
  const selectedNode = skeleton
    ? null
    : (graph.nodes.find((node) => node.id === selectedNodeId) ?? null);
  // Closing from the panel (X) or the keyboard (Escape) hands focus back to
  // the node that opened it, so the merchant is not dropped at the page top.
  const closeDetails = () => {
    const openerId = selectedNodeId;
    setSelectedNodeId(() => null);
    const opener = [
      ...(canvasRef.current?.querySelectorAll<HTMLElement>("[data-node-id]") ??
        []),
    ].find((element) => element.dataset.nodeId === openerId);
    opener?.focus();
  };
  const onCanvasKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || selectedNode === null) {
      return;
    }
    event.stopPropagation();
    closeDetails();
  };
  // The run walk's cursor, for the follow camera. Scanning the overlay map
  // here keeps the walk's plumbing entirely inside the overlay seam.
  const activeNodeId = skeleton
    ? null
    : (Object.entries(overlaysByNodeId ?? {}).find(
        ([, overlay]) => overlay.active === true
      )?.[0] ?? null);
  const reducedMotion = useReducedMotion();
  const animatedGraphKey = useRef<string | null>(null);
  const laneAnimations = useRef<Array<{ stop: () => void }>>([]);
  useEffect(
    () => () => laneAnimations.current.forEach((run) => run.stop()),
    []
  );
  useLayoutEffect(() => {
    if (!revealBuilt || !canvasReady || animatedGraphKey.current === graphKey) {
      return;
    }
    const elements = canvasRef.current?.querySelectorAll<HTMLElement>(
      ".react-flow__node[data-id]"
    );
    if (!elements?.length) {
      return;
    }
    animatedGraphKey.current = graphKey;
    laneAnimations.current.forEach((run) => run.stop());
    if (reducedMotion) {
      return;
    }
    const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
    laneAnimations.current = Array.from(elements, (element) =>
      animate(
        element,
        { opacity: [0, 1] },
        {
          duration: 0.24,
          delay: ruleLaneDelay(
            nodesById.get(element.dataset.id ?? ""),
            layout.laneOrder
          ),
          ease: [0, 0, 0.2, 1],
        }
      )
    );
  }, [
    canvasReady,
    graph.nodes,
    graphKey,
    layout.laneOrder,
    reducedMotion,
    revealBuilt,
  ]);

  return (
    <Stack
      data-testid="rule-graph-view"
      data-layout-ready={geometry.ready && canvasReady ? "true" : "false"}
      direction="row"
      // ReactFlow's parent, and the element whose size the canvas takes. Both
      // `FitOnReveal` and `RuleGraphRunCamera` measure through this ref: the
      // first to notice the no-size -> size transition a hidden tab body
      // produces, the second to judge whether the walk's active node is still
      // on screen. Neither can do anything while it is unattached.
      ref={canvasRef}
      onKeyDown={onCanvasKeyDown}
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightMid,
        border: `1px solid ${theme.igBorders.core.disabled}`,
        borderRadius: 3,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {analysis.issues.length > 0 ? (
        <Box
          role="status"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 3,
            backgroundColor: theme.igBackgroundColors.baseLightHigh,
            padding: 1,
          }}
        >
          Some duplicate or missing graph elements could not be shown.
        </Box>
      ) : null}
      {!canvasReady ? (
        <Stack
          role={skeleton ? undefined : "status"}
          aria-hidden={skeleton || undefined}
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            backgroundColor: theme.igBackgroundColors.baseLightMid,
          }}
        >
          {geometry.error ? (
            <>
              Could not arrange this graph.
              <Box component="button" onClick={geometry.retry}>
                Retry layout
              </Box>
            </>
          ) : (
            "Arranging graph…"
          )}
        </Stack>
      ) : null}
      <ReactFlow
        inert={!canvasReady}
        aria-hidden={!canvasReady}
        edgeTypes={RULE_GRAPH_EDGE_TYPES}
        edges={edges}
        edgesFocusable={false}
        elementsSelectable={false}
        maxZoom={RULE_GRAPH_MAX_ZOOM}
        minZoom={minZoom}
        nodeTypes={RULE_GRAPH_NODE_TYPES}
        nodes={nodes}
        nodesConnectable={false}
        nodesDraggable={false}
        nodesFocusable={false}
        onMoveStart={(event) => {
          if (event !== null) {
            setHasViewportMoved(() => true);
          }
        }}
        onPaneClick={() => setSelectedNodeId(() => null)}
        panOnDrag
        panOnScroll
        proOptions={{ hideAttribution: true }}
        zoomOnDoubleClick={false}
      >
        <RuleGraphViewportSync
          canvasRef={canvasRef}
          ready={geometry.ready}
          revision={geometry.revision}
          graphKey={graphKey}
          minZoom={minZoom}
          onMinZoom={setMinZoom}
          positions={geometry.geometry?.positionsByNodeId}
          bounds={geometry.geometry?.bounds}
          onApplied={setAppliedRevision}
        />
        <Background />
        <RuleGraphRunCamera
          activeNodeId={geometry.ready && canvasReady ? activeNodeId : null}
          layoutRevision={geometry.revision}
          canvasRef={canvasRef}
          onViewportMoved={() => setHasViewportMoved(() => true)}
        />
        <RuleGraphDetailsPanelSync
          bounds={geometry.geometry?.bounds}
          canvasRef={canvasRef}
          minZoom={minZoom}
          selectedNode={selectedNode}
        />
        <RuleGraphViewportControls
          bounds={geometry.geometry?.bounds}
          minZoom={minZoom}
          onViewportMoved={() => setHasViewportMoved(() => true)}
          onViewportReset={() => setHasViewportMoved(() => false)}
          showBackToStart={hasViewportMoved}
        />
      </ReactFlow>
      {selectedNode ? (
        <RuleGraphNodeDetailsPanel
          id={detailsPanelId}
          node={selectedNode}
          onClose={closeDetails}
          overlay={overlaysByNodeId?.[selectedNode.id]}
        />
      ) : null}
    </Stack>
  );
};
